import { Injectable, inject } from '@angular/core';
import { ICallableObject, RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Logger } from '@df/core/service/logger';
import { Status } from '@df/core/status';
import { IAddressBasicData } from '@df/session/api/address-book/address';
import { CustomerModel } from '@df/session/api/customer.model';
import { toDate } from '@df/util/fn/to-date';
import omit from 'lodash-es/omit';

export const enum ECustomerGroup {
    trade = 4
}

/**
 * Shown to the customer when a bulk download fails. The raw rejection is not usable
 * here: the archive endpoint is fronted by a proxy that returns a stock HTML error
 * page on timeout, and `Status.error` passes any string it is given straight through
 * to the notification modal, so assigning the caught value renders that page as text.
 * Selecting fewer documents is the one action that reliably helps, so say that.
 */
const BULK_DOWNLOAD_ERROR = 'We could not prepare your download. Please try again with fewer documents selected.';

declare module '@df/core/app' {
    interface App {
        trade: TradeService;
    }
}

export namespace NTrade {
    export namespace History {
        export type ItemType = 'order' | 'invoice';

        export type Item = Order | Invoice;

        export interface ConvertedItem extends Omit<Api.EntryData, 'dispatch_date' | 'order_date' | 'due_date'> {
            order_date: Date;
            dispatch_date?: Date;
            due_date?: Date;
        }

        export interface Order extends ConvertedItem {
            type: 'order';
            status?: Status;
        }

        export interface Invoice extends ConvertedItem {
            type: 'invoice';
        }

        export interface CreditNote extends Omit<Api.CreditNoteData, 'order_date' | 'dispatch_date' | 'due_date'> {
            type: 'credit-note';
            unallocated_amount: number;
            order_date: Date;
            dispatch_date?: Date;
            due_date?: Date;
        }

        export enum Status {
            Open = 'open',
            Closed = 'closed'
        }

        export interface CreditNotesData {
            items: CreditNote[];
            totalCount: number;
        }
    }

    export namespace Api {
        export type LoadOrdersResult = {
            orders: EntryData[];
            total_count: number;
        };

        export type LoadInvoicesResult = {
            invoices: EntryData[];
            total_count: number;
        };

        export type LoadCreditNotesResult = {
            credit_notes: CreditNoteData[];
            total_count: number;
        };

        export interface CreditNoteData {
            amount_redeemed: number;
            branch?: string;
            branch_code?: number;
            credit_note_number: string;
            custreference: string;
            delivery_address: IAddressBasicData;
            delivery_notes: string;
            delivery_sub_total: number;
            delivery_vat: number;
            delivery_total: number;
            dispatch_date: string;
            due_date: string;
            id: number;
            invoice_address: IAddressBasicData;
            items: ItemData[];
            order_date: string;
            order_notes: string;
            order_type: string;
            reference: string;
            sales_Rep: string;
            sub_total: number;
            total: number;
            vat: number;
        }

        export interface EntryData {
            id: number;
            sales_order: string;
            invoice_number: string;
            custreference: string;
            order_type: string;
            items: ItemData[];
            delivery_address: IAddressBasicData;
            invoice_address: IAddressBasicData;
            order_date: string;
            due_date: string;
            dispatch_date: string;
            delivery_sub_total: number;
            delivery_vat: number;
            delivery_total: number;
            sub_total: number;
            vat: number;
            total: number;
            reference: string;
            order_notes: string;
            delivery_notes: string;
            sales_rep: string;
            branch?: string;
            branch_code?: number;
            ammount_due?: number;
            delivery: string;
            // alternate_reference: string;
            // workflow_status: string;
        }

        export interface ItemData {
            id: number;
            code: string | number;
            description: string;
            qty: number;
            tax: number;
            sub_total: number;
            grand_total: number;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class TradeService {
    private customerModel = inject(CustomerModel);
    private requestService = inject(RequestService);
    private logger = inject(Logger);

    private readonly downloadUrls = new Map<NTrade.History.Item | NTrade.History.CreditNote, string>();

    constructor(app: App) {
        app.trade = this;
    }

    isTradeCustomer(customer: CustomerModel = this.customerModel): boolean {
        return customer.data?.group_id === ECustomerGroup.trade;
    }

    /**
     * Customer code used with some BE logic
     */
    get iqCustomerCode(): string | undefined {
        return this.customerModel.data?.customer_trade_account;
    }

    /**
     * Load history items (invoices or orders)
     */
    async loadHistoryItems(
        type: NTrade.History.ItemType,
        page?: number,
        status?: Status,
        search?: string,
        dateFrom?: string,
        dateTo?: string,
        workflowStatus?: NTrade.History.Status | 'ALL'
    ): Promise<{ items: NTrade.History.Item[]; totalCount: number } | undefined> {
        const iqCustomerCode = this.iqCustomerCode;

        if (!iqCustomerCode) {
            return undefined;
        }
        // load orders
        try {
            const result = await this.requestService.send(
                status,
                {
                    path: type === 'invoice' ? 'sales/getInvoices' : 'sales/getOrders',
                    data: {
                        iq_customer_code: this.iqCustomerCode,
                        'filters[limit]': 30,
                        'filters[start]': page ? (page - 1) * 30 : 0,
                        'filters[search]': search,
                        'filters[date_from]': dateFrom,
                        'filters[date_to]': dateTo,
                        'filters[workflow_status]': workflowStatus !== 'ALL' ? workflowStatus : undefined
                    }
                },
                undefined,
                undefined,
                { method: 'GET' }
            );

            if (type === 'invoice') {
                const typeResult = result as NTrade.Api.LoadInvoicesResult;
                return {
                    items: typeResult.invoices?.map(i => this.normalizeInvoice(i)),
                    totalCount: typeResult.total_count
                };
            } else {
                const typeResult = result as NTrade.Api.LoadOrdersResult;
                return {
                    items: typeResult.orders?.map(i => this.normalizeOrder(i)),
                    totalCount: typeResult.total_count
                };
            }
        } catch (err) {
            this.logger.error(err);
            return undefined;
        }
    }

    /**
     * Get invoice by id
     */
    async retrieveOrder(orderId: string): Promise<NTrade.History.Item | undefined> {
        const orders = await this.loadHistoryItems('order', 1, undefined, orderId);
        return orders?.items?.[0];
    }

    /**
     * Get invoice by id
     */
    async retrieveInvoice(invoiceNumer: string): Promise<NTrade.History.Item | undefined> {
        const invoices = await this.loadHistoryItems('invoice', 1, undefined, invoiceNumer);
        return invoices?.items?.[0];
    }

    /**
     * Load credit notes
     */
    async loadCreditNotes(request: {
        pageSize: number;
        page: number;
        status?: Status;
        search?: string;
        dateFrom?: string;
        dateTo?: string;
        redeemableOnly?: boolean;
    }): Promise<NTrade.History.CreditNotesData | undefined> {
        const iqCustomerCode = this.iqCustomerCode;

        if (!iqCustomerCode) {
            return undefined;
        }

        const data = {
            iq_customer_code: this.iqCustomerCode,
            'filters[limit]': request.pageSize,
            'filters[start]': request.page ? (request.page - 1) * request.pageSize : 0,
            'filters[search]': request.search,
            'filters[date_from]': request.dateFrom,
            'filters[date_to]': request.dateTo
        };

        if (request.redeemableOnly) {
            data['filters[only_redeemable]'] = true;
        }

        const result = await this.requestService.send(
            request.status,
            {
                path: 'sales/getCreditNotes',
                data
            },
            undefined,
            undefined,
            { method: 'GET' }
        );

        const typeResult = result as NTrade.Api.LoadCreditNotesResult;
        return {
            items: typeResult.credit_notes?.map(i => this.normalizeCreditNote(i)) || [],
            totalCount: typeResult.total_count || 0
        };
    }

    /**
     * Get credit note by id
     */
    async retrieveCreditNote(creditNoteNumber: string): Promise<NTrade.History.CreditNote | undefined> {
        const creditNotes = await this.loadCreditNotes({ page: 1, pageSize: 1, search: creditNoteNumber });
        return creditNotes?.items?.[0];
    }

    private async getDownloadUrl(item: NTrade.History.Item | NTrade.History.CreditNote): Promise<string | undefined> {
        if (!this.downloadUrls.has(item)) {
            let path: string;
            let idKey: string;
            switch (item.type) {
                case 'invoice':
                    path = 'sales/downloadinvoice';
                    idKey = 'invoice_id';
                    break;
                case 'order':
                    path = 'sales/downloadorder';
                    idKey = 'order_id';
                    break;
                case 'credit-note':
                    path = 'sales/downloadcreditnote';
                    idKey = 'credit_note_id';
                    break;
                default:
                    throw new Error('Invalid item type');
            }

            const request: ICallableObject = { path, data: { [idKey]: item.id } };
            const result = await this.requestService.send<{ file_url: string }>(undefined, request, undefined, undefined, {
                method: 'GET'
            });
            this.downloadUrls.set(item, result.file_url);
        }
        return this.downloadUrls.get(item);
    }

    async download(item: NTrade.History.Item | NTrade.History.CreditNote, status: Status): Promise<void> {
        status.busy = true;
        try {
            const downloadUrl = await this.getDownloadUrl(item);
            if (!downloadUrl) {
                throw new Error('Filed to load invoice URL');
            }
            window.open(downloadUrl, '_blank');
            status.busy = false;
        } catch (err) {
            status.error = err as any;
        }
    }

    async bulkDownloadInvoices(items: NTrade.History.Item[], status: Status): Promise<void> {
        status.busy = true;
        try {
            const request: ICallableObject = {
                path: 'sales/bulkDownloadInvoices',
                data: { invoice_ids: items.map(item => item.id) }
            };
            const result = await this.requestService.send<{ file_url: string }>(undefined, request, undefined, undefined, {
                method: 'POST'
            });
            if (!result?.file_url) {
                throw new Error('Failed to load bulk invoice URL');
            }
            this.triggerDownload(result.file_url);
            status.busy = false;
        } catch (err) {
            status.error = BULK_DOWNLOAD_ERROR;
            console.error('Bulk invoice download failed', err);
        }
    }

    /**
     * Download multiple credit notes in one request (BE returns a single file URL).
     */
    async bulkDownloadCreditNotes(ids: number[], status: Status): Promise<void> {
        status.busy = true;
        try {
            const request: ICallableObject = { path: 'sales/bulkDownloadCreditNotes', data: { credit_note_ids: ids } };
            const result = await this.requestService.send<{ file_url: string }>(undefined, request, undefined, undefined, {
                method: 'POST'
            });
            if (!result?.file_url) {
                throw new Error('Failed to load bulk credit notes URL');
            }
            this.triggerDownload(result.file_url);
            status.busy = false;
        } catch (err) {
            status.error = BULK_DOWNLOAD_ERROR;
            console.error('Bulk credit note download failed', err);
        }
    }

    /**
     * Starts a file download in the current tab. Bulk archives are ready only after the request
     * resolves, often past the browser's user-activation window, so `window.open` would be
     * blocked as a pop-up. A same-tab link click is not.
     */
    private triggerDownload(url: string): void {
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    private entryDataToConvertedItem(item: NTrade.Api.EntryData): NTrade.History.ConvertedItem {
        return {
            ...omit(item, 'dispatch_date', 'order_date', 'due_date'),
            order_date: toDate(item.order_date)!,
            dispatch_date: toDate(item.dispatch_date),
            due_date: toDate(item.due_date)
        };
    }

    private normalizeInvoice(invoice: NTrade.Api.EntryData): NTrade.History.Invoice {
        return {
            ...this.entryDataToConvertedItem(invoice),
            type: 'invoice'
        };
    }

    private normalizeOrder(invoice: NTrade.Api.EntryData): NTrade.History.Order {
        return {
            ...this.entryDataToConvertedItem(invoice),
            type: 'order'
        };
    }

    private normalizeCreditNote(creditNote: NTrade.Api.CreditNoteData): NTrade.History.CreditNote {
        return {
            ...creditNote,
            type: 'credit-note',
            unallocated_amount: Math.max(-creditNote.amount_redeemed || 0, 0),
            order_date: toDate(creditNote.order_date)!,
            dispatch_date: toDate(creditNote.dispatch_date),
            due_date: toDate(creditNote.due_date)
        };
    }
}
